"use client";

import { c as _c } from "react/compiler-runtime";
import { useRef } from "react";
import Image from "next/image";
import { Link } from "@/components/LocaleLink";
import { motion, useScroll, useTransform } from "motion/react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Check, Crown, Flag, Lock, Mic, Sparkles } from "lucide-react";
import { useTranslation } from "@/lib/hooks/useTranslation";
import { useAuthStore } from "@/lib/store";
import { Logo } from "@/components/Logo";
import { LiveInterviewDemo } from "@/components/LiveInterviewDemo";
import { MegaMenu } from "@/components/MegaMenu";
import { BorderBeam } from "@/components/ui/border-beam";
import { ShimmerButton } from "@/components/ui/shimmer-button";
import { Particles } from "@/components/ui/particles";
import { HeroEcosystem } from "@/components/landing/HeroEcosystem";
import { SolutionsJourney } from "@/components/landing/SolutionsJourney";
import { VelyqIn30s } from "@/components/landing/VelyqIn30s";
import { WhyVelyq } from "@/components/landing/WhyVelyq";

/* ─────────────────────────────────────────────
   Parallax blobs (background)
   ───────────────────────────────────────────── */

function ParallaxBlobs() {
  const $ = _c(15);
  const ref = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      target: ref,
      offset: ["start end", "end start"]
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    scrollYProgress
  } = useScroll(t0);
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [0, 1];
    t2 = [-80, 80];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  const y1 = useTransform(scrollYProgress, t1, t2);
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [0, 1];
    t4 = [-40, 40];
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  const y2 = useTransform(scrollYProgress, t3, t4);
  let t5;
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [0, 1];
    t6 = [0, 25];
    $[5] = t5;
    $[6] = t6;
  } else {
    t5 = $[5];
    t6 = $[6];
  }
  const rotate = useTransform(scrollYProgress, t5, t6);
  let t7;
  if ($[7] !== rotate || $[8] !== y1) {
    t7 = <motion.div style={{
      y: y1,
      rotate
    }} className="absolute -right-20 top-20 h-[300px] w-[300px] rounded-full bg-primary/[0.06] blur-[100px]" />;
    $[7] = rotate;
    $[8] = y1;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== y2) {
    t8 = <motion.div style={{
      y: y2
    }} className="absolute -left-10 bottom-10 h-[200px] w-[200px] rounded-full bg-violet-500/[0.04] blur-[80px]" />;
    $[10] = y2;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t7 || $[13] !== t8) {
    t9 = <div ref={ref} className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">{t7}{t8}</div>;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  return t9;
}

/* ─────────────────────────────────────────────
   Motion helpers
   ───────────────────────────────────────────── */

const fadeUp = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring" as const,
      damping: 22,
      stiffness: 120
    }
  }
};
const stagger = {
  visible: {
    transition: {
      staggerChildren: 0.07,
      delayChildren: 0.1
    }
  }
};

/* ─────────────────────────────────────────────
   Page
   ───────────────────────────────────────────── */

export default function Home() {
  const $ = _c(303);
  const {
    tObj,
    locale
  } = useTranslation();
  const t = tObj.landing;
  const {
    isAuthenticated,
    _hasHydrated
  } = useAuthStore();
  const authed = _hasHydrated && isAuthenticated;
  const ctaHref = authed ? "/dashboard" : "/register";
  const ctaLabel = authed ? tObj.nav.dashboard : t.hero.cta;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Link href="/" className="shrink-0"><Logo showBeta={true} /></Link>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== authed || $[2] !== locale) {
    t1 = <MegaMenu locale={locale} isAuthenticated={authed} />;
    $[1] = authed;
    $[2] = locale;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = locale === "fr" ? "D\xE9mo" : "Demo";
  let t3;
  if ($[4] !== t2) {
    t3 = {
      href: "#demo",
      label: t2,
      minW: "min-w-[44px]"
    };
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t.pricing.title) {
    t4 = {
      href: "#pricing",
      label: t.pricing.title,
      minW: "min-w-[56px]"
    };
    $[6] = t.pricing.title;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      href: "#faq",
      label: "FAQ",
      minW: "min-w-[34px]"
    };
    t6 = {
      href: "/blog",
      label: "Blog",
      minW: "min-w-[36px]"
    };
    $[8] = t5;
    $[9] = t6;
  } else {
    t5 = $[8];
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t3 || $[11] !== t4) {
    t7 = [t3, t4, t5, t6].map(_temp);
    $[10] = t3;
    $[11] = t4;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t1 || $[14] !== t7) {
    t8 = <nav className="hidden items-center justify-center gap-6 lg:flex">{t1}{t7}</nav>;
    $[13] = t1;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="w-[60px] shrink-0 sm:w-[72px]"><LanguageSwitcher /></div>;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== authed || $[18] !== locale || $[19] !== t.hero.cta || $[20] !== tObj.auth || $[21] !== tObj.nav) {
    t10 = <div className="flex items-center justify-end gap-2 sm:gap-3">{t9}{authed ? <Link href="/dashboard"><Button size="sm" className="min-w-[140px]">{tObj.nav.dashboard}</Button></Link> : <><Link href="/login" className="hidden sm:block"><Button variant="ghost" size="sm" className="min-w-[96px]">{tObj.auth.login}</Button></Link><Link href="/register"><Button size="sm" className="min-w-[100px] lg:min-w-[210px]"><span className="hidden lg:inline">{t.hero.cta}</span><span className="lg:hidden">{locale === "fr" ? "S'inscrire" : "Sign up"}</span></Button></Link></>}</div>;
    $[17] = authed;
    $[18] = locale;
    $[19] = t.hero.cta;
    $[20] = tObj.auth;
    $[21] = tObj.nav;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t8) {
    t11 = <header className="sticky top-0 z-50 w-full border-b border-border/50 bg-background/80 backdrop-blur-xl"><div className="container mx-auto grid h-16 grid-cols-[auto_1fr_auto] items-center gap-4 px-4 md:gap-6">{t0}{t8}{t10}</div></header>;
    $[23] = t10;
    $[24] = t8;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== ctaHref || $[27] !== ctaLabel || $[28] !== locale || $[29] !== t.hero.badge || $[30] !== t.hero.reassurance || $[31] !== t.hero.subtitle) {
    t12 = <HeroEcosystem locale={locale} badge={t.hero.badge} subtitle={t.hero.subtitle} ctaLabel={ctaLabel} ctaHref={ctaHref} reassurance={t.hero.reassurance} />;
    $[26] = ctaHref;
    $[27] = ctaLabel;
    $[28] = locale;
    $[29] = t.hero.badge;
    $[30] = t.hero.reassurance;
    $[31] = t.hero.subtitle;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Flag className="h-4 w-4 text-primary" />;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== t.trustBar.item1) {
    t14 = <div className="flex items-center gap-2">{t13}<span>{t.trustBar.item1}</span></div>;
    $[34] = t.trustBar.item1;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <span className="hidden h-1 w-1 rounded-full bg-border md:block" />;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Lock className="h-4 w-4 text-primary" />;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t.trustBar.item2) {
    t17 = <div className="flex items-center gap-2">{t16}<span>{t.trustBar.item2}</span></div>;
    $[38] = t.trustBar.item2;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <span className="hidden h-1 w-1 rounded-full bg-border md:block" />;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <Sparkles className="h-4 w-4 text-primary" />;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== t.trustBar.item3) {
    t20 = <div className="flex items-center gap-2">{t19}<span>{t.trustBar.item3}</span></div>;
    $[42] = t.trustBar.item3;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== t14 || $[45] !== t17 || $[46] !== t20) {
    t21 = <section className="border-y border-border/30 bg-card/20 py-6"><div className="container mx-auto px-4"><div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-sm text-muted-foreground">{t14}{t15}{t17}{t18}{t20}</div></div></section>;
    $[44] = t14;
    $[45] = t17;
    $[46] = t20;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== locale) {
    t22 = <VelyqIn30s locale={locale} />;
    $[48] = locale;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== authed || $[51] !== locale) {
    t23 = <SolutionsJourney locale={locale} isAuthenticated={authed} />;
    $[50] = authed;
    $[51] = locale;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  let t24;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <ParallaxBlobs />;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = {
      once: true,
      margin: "-80px"
    };
    $[54] = t25;
  } else {
    t25 = $[54];
  }
  let t26;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <span className="relative flex h-1.5 w-1.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#d946ef]/80" /><span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#d946ef]" /></span>;
    $[55] = t26;
  } else {
    t26 = $[55];
  }
  const t27 = locale === "fr" ? "D\xE9mo live \xB7 sans inscription" : "Live demo \xB7 no signup";
  let t28;
  if ($[56] !== t27) {
    t28 = <motion.div variants={fadeUp} className="mb-6 flex justify-center"><span className="inline-flex items-center gap-1.5 rounded-full border border-[#d946ef]/40 bg-[#d946ef]/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-[#d946ef] backdrop-blur">{t26}{t27}</span></motion.div>;
    $[56] = t27;
    $[57] = t28;
  } else {
    t28 = $[57];
  }
  let t29;
  if ($[58] !== locale) {
    t29 = <motion.h2 variants={fadeUp} className="mb-3 text-center font-display text-3xl font-bold leading-tight tracking-tight text-balance sm:text-4xl md:text-5xl">{locale === "fr" ? <>Regarde Velyq travailler{" "}<span className="bg-gradient-to-br from-[#d946ef] via-primary to-[#c084fc] bg-clip-text text-transparent">pour toi</span></> : <>Watch Velyq work{" "}<span className="bg-gradient-to-br from-[#d946ef] via-primary to-[#c084fc] bg-clip-text text-transparent">for you</span></>}</motion.h2>;
    $[58] = locale;
    $[59] = t29;
  } else {
    t29 = $[59];
  }
  const t30 = locale === "fr" ? "Un aper\xE7u de l'\xE9cosyst\xE8me en action : simulation d'entretien en direct, scoring temps r\xE9el, suggestions contextuelles. C'est l'un des 8 outils \u2014 essaie maintenant." : "A preview of the ecosystem in action: live interview simulation, real-time scoring, contextual suggestions. One of 8 tools \u2014 try it now.";
  let t31;
  if ($[60] !== t30) {
    t31 = <motion.p variants={fadeUp} className="mx-auto mb-12 max-w-xl text-center text-sm text-muted-foreground sm:text-base">{t30}</motion.p>;
    $[60] = t30;
    $[61] = t31;
  } else {
    t31 = $[61];
  }
  let t32;
  if ($[62] !== locale) {
    t32 = <motion.div variants={fadeUp}><LiveInterviewDemo locale={locale} /></motion.div>;
    $[62] = locale;
    $[63] = t32;
  } else {
    t32 = $[63];
  }
  const t33 = authed ? "/interviews/new?type=live" : "/register";
  let t34;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <Mic className="h-4 w-4" />;
    $[64] = t34;
  } else {
    t34 = $[64];
  }
  const t35 = locale === "fr" ? "Lancer un vrai entretien" : "Start a real interview";
  let t36;
  if ($[65] !== t35) {
    t36 = <Button size="lg" variant="outline" className="gap-2">{t34}{t35}</Button>;
    $[65] = t35;
    $[66] = t36;
  } else {
    t36 = $[66];
  }
  let t37;
  if ($[67] !== t33 || $[68] !== t36) {
    t37 = <motion.div variants={fadeUp} className="mt-8 text-center"><Link href={t33}>{t36}</Link></motion.div>;
    $[67] = t33;
    $[68] = t36;
    $[69] = t37;
  } else {
    t37 = $[69];
  }
  let t38;
  if ($[70] !== t28 || $[71] !== t29 || $[72] !== t31 || $[73] !== t32 || $[74] !== t37) {
    t38 = <section id="demo" className="noise relative overflow-hidden border-t border-border/30 py-20 sm:py-28">{t24}<div className="container relative mx-auto px-4"><motion.div initial="hidden" whileInView="visible" viewport={t25} variants={stagger}>{t28}{t29}{t31}{t32}{t37}</motion.div></div></section>;
    $[70] = t28;
    $[71] = t29;
    $[72] = t31;
    $[73] = t32;
    $[74] = t37;
    $[75] = t38;
  } else {
    t38 = $[75];
  }
  let t39;
  if ($[76] !== locale) {
    t39 = <WhyVelyq locale={locale} />;
    $[76] = locale;
    $[77] = t39;
  } else {
    t39 = $[77];
  }
  let t40;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <div className="pointer-events-none absolute inset-0 -z-10"><div className="absolute left-1/2 top-1/3 h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-primary/[0.06] blur-[120px]" /></div>;
    $[78] = t40;
  } else {
    t40 = $[78];
  }
  let t41;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = {
      once: true,
      margin: "-80px"
    };
    $[79] = t41;
  } else {
    t41 = $[79];
  }
  let t42;
  if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <span className="h-1.5 w-1.5 rounded-full bg-primary" />;
    $[80] = t42;
  } else {
    t42 = $[80];
  }
  const t43 = locale === "fr" ? "Tarifs simples" : "Simple pricing";
  let t44;
  if ($[81] !== t43) {
    t44 = <motion.div variants={fadeUp}><span className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/[0.08] px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-primary">{t42}{t43}</span></motion.div>;
    $[81] = t43;
    $[82] = t44;
  } else {
    t44 = $[82];
  }
  const t45 = locale === "fr" ? "Commence gratuit. Upgrade quand tu veux." : "Start free. Upgrade whenever.";
  let t46;
  if ($[83] !== t45) {
    t46 = <motion.h2 variants={fadeUp} className="mt-4 font-display text-3xl font-bold tracking-tight text-balance sm:text-4xl md:text-5xl">{t45}</motion.h2>;
    $[83] = t45;
    $[84] = t46;
  } else {
    t46 = $[84];
  }
  const t47 = locale === "fr" ? "Pas de carte bancaire requise pour commencer. Annule en 1 clic." : "No credit card required to start. Cancel in one click.";
  let t48;
  if ($[85] !== t47) {
    t48 = <motion.p variants={fadeUp} className="mx-auto mt-3 max-w-xl text-sm text-muted-foreground sm:text-base">{t47}</motion.p>;
    $[85] = t47;
    $[86] = t48;
  } else {
    t48 = $[86];
  }
  let t49;
  if ($[87] !== t44 || $[88] !== t46 || $[89] !== t48) {
    t49 = <motion.div initial="hidden" whileInView="visible" viewport={t41} variants={stagger} className="mb-14 text-center">{t44}{t46}{t48}</motion.div>;
    $[87] = t44;
    $[88] = t46;
    $[89] = t48;
    $[90] = t49;
  } else {
    t49 = $[90];
  }
  let t50;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = {
      once: true,
      margin: "-60px"
    };
    $[91] = t50;
  } else {
    t50 = $[91];
  }
  let t51;
  if ($[92] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-border/60 bg-muted/30"><Sparkles className="h-4 w-4 text-muted-foreground" /></div>;
    $[92] = t51;
  } else {
    t51 = $[92];
  }
  let t52;
  if ($[93] !== t.pricing.free.name) {
    t52 = <div className="flex items-center gap-2">{t51}<h3 className="font-display text-lg font-bold">{t.pricing.free.name}</h3></div>;
    $[93] = t.pricing.free.name;
    $[94] = t52;
  } else {
    t52 = $[94];
  }
  let t53;
  if ($[95] !== t.pricing.free.description) {
    t53 = <p className="mt-2 text-xs text-muted-foreground">{t.pricing.free.description}</p>;
    $[95] = t.pricing.free.description;
    $[96] = t53;
  } else {
    t53 = $[96];
  }
  let t54;
  if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = <span className="font-display text-4xl font-bold tracking-tight">0€</span>;
    $[97] = t54;
  } else {
    t54 = $[97];
  }
  const t55 = locale === "fr" ? "toujours" : "forever";
  let t56;
  if ($[98] !== t55) {
    t56 = <div className="mt-4 flex items-baseline gap-1">{t54}<span className="text-xs text-muted-foreground">/{t55}</span></div>;
    $[98] = t55;
    $[99] = t56;
  } else {
    t56 = $[99];
  }
  let t57;
  if ($[100] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = <div className="my-5 h-px bg-border/40" />;
    $[100] = t57;
  } else {
    t57 = $[100];
  }
  let t58;
  if ($[101] !== t.pricing.free.features) {
    t58 = t.pricing.free.features.map(_temp2);
    $[101] = t.pricing.free.features;
    $[102] = t58;
  } else {
    t58 = $[102];
  }
  let t59;
  if ($[103] !== t58) {
    t59 = <ul className="flex-1 space-y-2.5">{t58}</ul>;
    $[103] = t58;
    $[104] = t59;
  } else {
    t59 = $[104];
  }
  let t60;
  if ($[105] !== ctaLabel) {
    t60 = <Button variant="outline" className="w-full">{ctaLabel}</Button>;
    $[105] = ctaLabel;
    $[106] = t60;
  } else {
    t60 = $[106];
  }
  let t61;
  if ($[107] !== ctaHref || $[108] !== t60) {
    t61 = <Link href={ctaHref} className="mt-6 block">{t60}</Link>;
    $[107] = ctaHref;
    $[108] = t60;
    $[109] = t61;
  } else {
    t61 = $[109];
  }
  let t62;
  if ($[110] !== t52 || $[111] !== t53 || $[112] !== t56 || $[113] !== t59 || $[114] !== t61) {
    t62 = <motion.div variants={fadeUp} className="md:mt-6"><div className="flex h-full flex-col rounded-2xl border border-border/40 bg-card/50 p-6 backdrop-blur-sm">{t52}{t53}{t56}{t57}{t59}{t61}</div></motion.div>;
    $[110] = t52;
    $[111] = t53;
    $[112] = t56;
    $[113] = t59;
    $[114] = t61;
    $[115] = t62;
  } else {
    t62 = $[115];
  }
  let t63;
  if ($[116] === Symbol.for("react.memo_cache_sentinel")) {
    t63 = <BorderBeam size={220} duration={8} colorFrom="#a855f7" colorTo="#6366f1" borderWidth={1.5} />;
    $[116] = t63;
  } else {
    t63 = $[116];
  }
  const t64 = locale === "fr" ? "Le plus choisi" : "Most popular";
  let t65;
  if ($[117] !== t64) {
    t65 = <div className="absolute -top-px left-1/2 -translate-x-1/2 rounded-b-lg bg-gradient-to-b from-primary to-violet-600 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow-lg">{t64}</div>;
    $[117] = t64;
    $[118] = t65;
  } else {
    t65 = $[118];
  }
  let t66;
  if ($[119] === Symbol.for("react.memo_cache_sentinel")) {
    t66 = <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-primary/40 bg-gradient-to-br from-primary/20 to-primary/5"><Crown className="h-4 w-4 text-primary" /></div>;
    $[119] = t66;
  } else {
    t66 = $[119];
  }
  let t67;
  if ($[120] !== t.pricing.premium.name) {
    t67 = <div className="flex items-center gap-2">{t66}<h3 className="font-display text-lg font-bold">{t.pricing.premium.name}</h3></div>;
    $[120] = t.pricing.premium.name;
    $[121] = t67;
  } else {
    t67 = $[121];
  }
  let t68;
  if ($[122] !== t.pricing.premium.description) {
    t68 = <p className="mt-2 text-xs text-muted-foreground">{t.pricing.premium.description}</p>;
    $[122] = t.pricing.premium.description;
    $[123] = t68;
  } else {
    t68 = $[123];
  }
  let t69;
  if ($[124] === Symbol.for("react.memo_cache_sentinel")) {
    t69 = <span className="font-display text-4xl font-bold tracking-tight">9,99€</span>;
    $[124] = t69;
  } else {
    t69 = $[124];
  }
  const t70 = locale === "fr" ? "mois" : "mo";
  let t71;
  if ($[125] !== t70) {
    t71 = <div className="mt-4 flex items-baseline gap-1">{t69}<span className="text-xs text-muted-foreground">/{t70}</span></div>;
    $[125] = t70;
    $[126] = t71;
  } else {
    t71 = $[126];
  }
  let t72;
  if ($[127] === Symbol.for("react.memo_cache_sentinel")) {
    t72 = <div className="my-5 h-px bg-primary/20" />;
    $[127] = t72;
  } else {
    t72 = $[127];
  }
  let t73;
  if ($[128] !== t.pricing.premium.features) {
    t73 = t.pricing.premium.features.map(_temp3);
    $[128] = t.pricing.premium.features;
    $[129] = t73;
  } else {
    t73 = $[129];
  }
  let t74;
  if ($[130] !== t73) {
    t74 = <ul className="flex-1 space-y-2.5">{t73}</ul>;
    $[130] = t73;
    $[131] = t74;
  } else {
    t74 = $[131];
  }
  const t75 = locale === "fr" ? "Passer Premium" : "Go Premium";
  let t76;
  if ($[132] !== t75) {
    t76 = <Button className="w-full">{t75}</Button>;
    $[132] = t75;
    $[133] = t76;
  } else {
    t76 = $[133];
  }
  let t77;
  if ($[134] !== ctaHref || $[135] !== t76) {
    t77 = <Link href={ctaHref} className="mt-6 block">{t76}</Link>;
    $[134] = ctaHref;
    $[135] = t76;
    $[136] = t77;
  } else {
    t77 = $[136];
  }
  let t78;
  if ($[137] !== t67 || $[138] !== t68 || $[139] !== t71 || $[140] !== t74 || $[141] !== t77) {
    t78 = <div className="relative mt-3">{t67}{t68}{t71}{t72}{t74}{t77}</div>;
    $[137] = t67;
    $[138] = t68;
    $[139] = t71;
    $[140] = t74;
    $[141] = t77;
    $[142] = t78;
  } else {
    t78 = $[142];
  }
  let t79;
  if ($[143] !== t65 || $[144] !== t78) {
    t79 = <motion.div variants={fadeUp}><div className="relative flex h-full flex-col overflow-hidden rounded-2xl border-2 border-primary/50 bg-gradient-to-b from-primary/[0.08] to-card/60 p-6 shadow-[0_20px_60px_-20px_rgba(168,85,247,0.4)] backdrop-blur-sm">{t63}{t65}{t78}</div></motion.div>;
    $[143] = t65;
    $[144] = t78;
    $[145] = t79;
  } else {
    t79 = $[145];
  }
  let t80;
  if ($[146] !== t62 || $[147] !== t79) {
    t80 = <motion.div initial="hidden" whileInView="visible" viewport={t50} variants={stagger} className="mx-auto grid max-w-3xl items-stretch gap-4 md:grid-cols-2">{t62}{t79}</motion.div>;
    $[146] = t62;
    $[147] = t79;
    $[148] = t80;
  } else {
    t80 = $[148];
  }
  let t81;
  let t82;
  let t83;
  let t84;
  if ($[149] === Symbol.for("react.memo_cache_sentinel")) {
    t81 = {
      opacity: 0,
      y: 10
    };
    t82 = {
      opacity: 1,
      y: 0
    };
    t83 = {
      once: true
    };
    t84 = {
      duration: 0.4,
      delay: 0.3
    };
    $[149] = t81;
    $[150] = t82;
    $[151] = t83;
    $[152] = t84;
  } else {
    t81 = $[149];
    t82 = $[150];
    t83 = $[151];
    t84 = $[152];
  }
  let t85;
  if ($[153] === Symbol.for("react.memo_cache_sentinel")) {
    t85 = <Check className="h-3.5 w-3.5 text-primary" />;
    $[153] = t85;
  } else {
    t85 = $[153];
  }
  const t86 = locale === "fr" ? "Sans carte bancaire" : "No credit card";
  let t87;
  if ($[154] !== t86) {
    t87 = <span className="flex items-center gap-1.5">{t85}{t86}</span>;
    $[154] = t86;
    $[155] = t87;
  } else {
    t87 = $[155];
  }
  let t88;
  if ($[156] === Symbol.for("react.memo_cache_sentinel")) {
    t88 = <Check className="h-3.5 w-3.5 text-primary" />;
    $[156] = t88;
  } else {
    t88 = $[156];
  }
  const t89 = locale === "fr" ? "Annule en 1 clic" : "Cancel in 1 click";
  let t90;
  if ($[157] !== t89) {
    t90 = <span className="flex items-center gap-1.5">{t88}{t89}</span>;
    $[157] = t89;
    $[158] = t90;
  } else {
    t90 = $[158];
  }
  let t91;
  if ($[159] === Symbol.for("react.memo_cache_sentinel")) {
    t91 = <Check className="h-3.5 w-3.5 text-primary" />;
    $[159] = t91;
  } else {
    t91 = $[159];
  }
  const t92 = locale === "fr" ? "Support FR" : "FR support";
  let t93;
  if ($[160] !== t92) {
    t93 = <span className="flex items-center gap-1.5">{t91}{t92}</span>;
    $[160] = t92;
    $[161] = t93;
  } else {
    t93 = $[161];
  }
  let t94;
  if ($[162] !== t87 || $[163] !== t90 || $[164] !== t93) {
    t94 = <motion.div initial={t81} whileInView={t82} viewport={t83} transition={t84} className="mx-auto mt-10 flex max-w-md flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-muted-foreground">{t87}{t90}{t93}</motion.div>;
    $[162] = t87;
    $[163] = t90;
    $[164] = t93;
    $[165] = t94;
  } else {
    t94 = $[165];
  }
  let t95;
  if ($[166] !== t49 || $[167] !== t80 || $[168] !== t94) {
    t95 = <section id="pricing" className="relative overflow-hidden border-t border-border/30 bg-card/10 py-20 sm:py-28">{t40}<div className="container relative mx-auto px-4">{t49}{t80}{t94}</div></section>;
    $[166] = t49;
    $[167] = t80;
    $[168] = t94;
    $[169] = t95;
  } else {
    t95 = $[169];
  }
  let t96;
  if ($[170] === Symbol.for("react.memo_cache_sentinel")) {
    t96 = {
      once: true,
      margin: "-80px"
    };
    $[170] = t96;
  } else {
    t96 = $[170];
  }
  let t97;
  if ($[171] !== t.faq.title) {
    t97 = <motion.h2 initial="hidden" whileInView="visible" viewport={t96} variants={fadeUp} className="mb-12 text-center font-display text-2xl font-bold tracking-tight text-balance sm:text-3xl md:text-4xl">{t.faq.title}</motion.h2>;
    $[171] = t.faq.title;
    $[172] = t97;
  } else {
    t97 = $[172];
  }
  let t98;
  if ($[173] === Symbol.for("react.memo_cache_sentinel")) {
    t98 = {
      once: true,
      margin: "-60px"
    };
    $[173] = t98;
  } else {
    t98 = $[173];
  }
  let t99;
  if ($[174] === Symbol.for("react.memo_cache_sentinel")) {
    t99 = ["q1", "q2", "q3", "q4", "q5"];
    $[174] = t99;
  } else {
    t99 = $[174];
  }
  let t100;
  if ($[175] !== t.faq) {
    t100 = <motion.div initial="hidden" whileInView="visible" viewport={t98} variants={fadeUp} className="mx-auto max-w-xl"><Accordion type="single" collapsible={true} className="w-full">{(t99 as const).map(key => <AccordionItem key={key} value={key}><AccordionTrigger className="text-left text-sm">{t.faq[key].question}</AccordionTrigger><AccordionContent className="text-sm text-muted-foreground">{t.faq[key].answer}</AccordionContent></AccordionItem>)}</Accordion></motion.div>;
    $[175] = t.faq;
    $[176] = t100;
  } else {
    t100 = $[176];
  }
  let t101;
  if ($[177] !== t100 || $[178] !== t97) {
    t101 = <section id="faq" className="border-t border-border/30 py-20 sm:py-24"><div className="container mx-auto px-4">{t97}{t100}</div></section>;
    $[177] = t100;
    $[178] = t97;
    $[179] = t101;
  } else {
    t101 = $[179];
  }
  let t102;
  if ($[180] === Symbol.for("react.memo_cache_sentinel")) {
    t102 = <Particles className="absolute inset-0" quantity={60} color="#a855f7" size={0.6} staticity={30} ease={40} />;
    $[180] = t102;
  } else {
    t102 = $[180];
  }
  let t103;
  if ($[181] === Symbol.for("react.memo_cache_sentinel")) {
    t103 = <div className="pointer-events-none absolute inset-0 -z-10"><div className="absolute left-1/4 top-1/2 h-[500px] w-[500px] -translate-y-1/2 rounded-full bg-primary/[0.08] blur-[140px]" /><div className="absolute right-1/4 top-1/2 h-[400px] w-[400px] -translate-y-1/2 rounded-full bg-[#5278ff]/[0.06] blur-[120px]" /></div>;
    $[181] = t103;
  } else {
    t103 = $[181];
  }
  let t104;
  if ($[182] === Symbol.for("react.memo_cache_sentinel")) {
    t104 = {
      once: true,
      margin: "-80px"
    };
    $[182] = t104;
  } else {
    t104 = $[182];
  }
  let t105;
  let t106;
  let t107;
  if ($[183] === Symbol.for("react.memo_cache_sentinel")) {
    t105 = <BorderBeam size={280} duration={10} colorFrom="#c084fc" colorTo="#5278ff" borderWidth={1.5} />;
    t106 = <div className="pointer-events-none absolute -left-20 -top-20 h-[280px] w-[280px] rounded-full bg-primary/[0.25] blur-[80px]" />;
    t107 = <div className="pointer-events-none absolute -bottom-20 -right-20 h-[240px] w-[240px] rounded-full bg-[#5278ff]/[0.2] blur-[80px]" />;
    $[183] = t105;
    $[184] = t106;
    $[185] = t107;
  } else {
    t105 = $[183];
    t106 = $[184];
    t107 = $[185];
  }
  let t108;
  if ($[186] === Symbol.for("react.memo_cache_sentinel")) {
    t108 = <motion.div variants={fadeUp} className="relative mx-auto mb-6 flex h-20 w-20 items-center justify-center"><div className="absolute h-20 w-20 rounded-full border border-dashed border-primary/30" /><div className="absolute h-14 w-14 rounded-full border border-primary/20" /><Image src="/velyq_icon.png" alt="Velyq" width={56} height={56} className="relative z-10 h-14 w-14 object-contain drop-shadow-[0_0_24px_rgba(168,85,247,0.5)]" loading="lazy" /></motion.div>;
    $[186] = t108;
  } else {
    t108 = $[186];
  }
  let t109;
  if ($[187] !== locale) {
    t109 = <motion.h2 variants={fadeUp} className="font-display text-3xl font-bold leading-[1.05] tracking-tight text-balance sm:text-4xl md:text-5xl">{locale === "fr" ? <>Tu arrives avec un CV.<br />Tu repars avec{" "}<span className="bg-gradient-to-br from-[#c4b5fd] via-primary to-[#5278ff] bg-clip-text text-transparent">un contrat.</span></> : <>Start with a resume.<br />Leave with{" "}<span className="bg-gradient-to-br from-[#c4b5fd] via-primary to-[#5278ff] bg-clip-text text-transparent">a signed contract.</span></>}</motion.h2>;
    $[187] = locale;
    $[188] = t109;
  } else {
    t109 = $[188];
  }
  const t110 = locale === "fr" ? "8 outils connect\xE9s qui font le chemin avec toi. D\xE9marre gratuitement, sans carte bancaire \u2014 moins de 30 secondes." : "8 connected tools that walk with you. Start free, no credit card \u2014 under 30 seconds.";
  let t111;
  if ($[189] !== t110) {
    t111 = <motion.p variants={fadeUp} className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base">{t110}</motion.p>;
    $[189] = t110;
    $[190] = t111;
  } else {
    t111 = $[190];
  }
  const t112 = authed ? tObj.nav.dashboard : t.cta.button;
  let t113;
  if ($[191] === Symbol.for("react.memo_cache_sentinel")) {
    t113 = <ArrowRight className="ml-2 h-4 w-4" />;
    $[191] = t113;
  } else {
    t113 = $[191];
  }
  let t114;
  if ($[192] !== t112) {
    t114 = <ShimmerButton className="h-12 px-8" shimmerColor="#c084fc" background="linear-gradient(135deg, #a855f7 0%, #6366f1 100%)" borderRadius="14px"><span className="flex items-center text-[15px] font-semibold text-white">{t112}{t113}</span></ShimmerButton>;
    $[192] = t112;
    $[193] = t114;
  } else {
    t114 = $[193];
  }
  let t115;
  if ($[194] !== ctaHref || $[195] !== t114) {
    t115 = <Link href={ctaHref}>{t114}</Link>;
    $[194] = ctaHref;
    $[195] = t114;
    $[196] = t115;
  } else {
    t115 = $[196];
  }
  let t116;
  if ($[197] === Symbol.for("react.memo_cache_sentinel")) {
    t116 = <Check className="h-3.5 w-3.5 text-primary" />;
    $[197] = t116;
  } else {
    t116 = $[197];
  }
  const t117 = locale === "fr" ? "Gratuit" : "Free";
  let t118;
  if ($[198] !== t117) {
    t118 = <span className="flex items-center gap-1.5">{t116}{t117}</span>;
    $[198] = t117;
    $[199] = t118;
  } else {
    t118 = $[199];
  }
  let t119;
  if ($[200] === Symbol.for("react.memo_cache_sentinel")) {
    t119 = <Check className="h-3.5 w-3.5 text-primary" />;
    $[200] = t119;
  } else {
    t119 = $[200];
  }
  const t120 = locale === "fr" ? "Sans carte" : "No card";
  let t121;
  if ($[201] !== t120) {
    t121 = <span className="flex items-center gap-1.5">{t119}{t120}</span>;
    $[201] = t120;
    $[202] = t121;
  } else {
    t121 = $[202];
  }
  let t122;
  if ($[203] === Symbol.for("react.memo_cache_sentinel")) {
    t122 = <Check className="h-3.5 w-3.5 text-primary" />;
    $[203] = t122;
  } else {
    t122 = $[203];
  }
  const t123 = locale === "fr" ? "< 30 secondes" : "< 30 seconds";
  let t124;
  if ($[204] !== t123) {
    t124 = <span className="flex items-center gap-1.5">{t122}{t123}</span>;
    $[204] = t123;
    $[205] = t124;
  } else {
    t124 = $[205];
  }
  let t125;
  if ($[206] !== t118 || $[207] !== t121 || $[208] !== t124) {
    t125 = <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 text-xs text-muted-foreground">{t118}{t121}{t124}</div>;
    $[206] = t118;
    $[207] = t121;
    $[208] = t124;
    $[209] = t125;
  } else {
    t125 = $[209];
  }
  let t126;
  if ($[210] !== t115 || $[211] !== t125) {
    t126 = <motion.div variants={fadeUp} className="mt-8 flex flex-col items-center gap-4">{t115}{t125}</motion.div>;
    $[210] = t115;
    $[211] = t125;
    $[212] = t126;
  } else {
    t126 = $[212];
  }
  let t127;
  if ($[213] !== t109 || $[214] !== t111 || $[215] !== t126) {
    t127 = <section className="noise relative overflow-hidden border-t border-border/30 py-20 sm:py-28">{t102}{t103}<div className="container relative z-10 mx-auto px-4"><motion.div initial="hidden" whileInView="visible" viewport={t104} variants={stagger} className="relative mx-auto max-w-3xl overflow-hidden rounded-3xl border border-primary/30 bg-gradient-to-br from-card/95 via-card/80 to-card/95 p-6 text-center backdrop-blur-xl sm:p-10 md:p-16">{t105}{t106}{t107}{t108}{t109}{t111}{t126}</motion.div></div></section>;
    $[213] = t109;
    $[214] = t111;
    $[215] = t126;
    $[216] = t127;
  } else {
    t127 = $[216];
  }
  let t128;
  if ($[217] === Symbol.for("react.memo_cache_sentinel")) {
    t128 = <Logo size="sm" />;
    $[217] = t128;
  } else {
    t128 = $[217];
  }
  const t129 = locale === "fr" ? "La plateforme carri\xE8re IA." : "The AI career platform.";
  let t130;
  if ($[218] !== t129) {
    t130 = <div className="md:col-span-1">{t128}<p className="mt-3 text-xs leading-relaxed text-muted-foreground">{t129}</p></div>;
    $[218] = t129;
    $[219] = t130;
  } else {
    t130 = $[219];
  }
  let t131;
  if ($[220] === Symbol.for("react.memo_cache_sentinel")) {
    t131 = <p className="mb-3 text-sm font-semibold">Solutions</p>;
    $[220] = t131;
  } else {
    t131 = $[220];
  }
  const t132 = authed ? "/interviews/new" : "/register";
  const t133 = locale === "fr" ? "Pr\xE9paration entretien" : "Interview Prep";
  let t134;
  if ($[221] !== t132 || $[222] !== t133) {
    t134 = <li><Link href={t132} className="transition-colors hover:text-foreground">{t133}</Link></li>;
    $[221] = t132;
    $[222] = t133;
    $[223] = t134;
  } else {
    t134 = $[223];
  }
  const t135 = locale === "fr" ? "Analyse CV ATS" : "ATS Resume Analysis";
  let t136;
  if ($[224] !== t135) {
    t136 = <li><Link href="/analyse-cv" className="transition-colors hover:text-foreground">{t135}</Link></li>;
    $[224] = t135;
    $[225] = t136;
  } else {
    t136 = $[225];
  }
  const t137 = authed ? "/resumes" : "/register";
  let t138;
  if ($[226] !== t137) {
    t138 = <li><Link href={t137} className="transition-colors hover:text-foreground">CV Builder</Link></li>;
    $[226] = t137;
    $[227] = t138;
  } else {
    t138 = $[227];
  }
  const t139 = locale === "fr" ? "Lettre de motivation IA" : "AI Cover Letter";
  const t140 = locale === "fr" ? "Bient\xF4t" : "Soon";
  let t141;
  if ($[228] !== t140) {
    t141 = <Badge variant="secondary" className="ml-1.5 px-1.5 py-0 text-[9px]">{t140}</Badge>;
    $[228] = t140;
    $[229] = t141;
  } else {
    t141 = $[229];
  }
  let t142;
  if ($[230] !== t139 || $[231] !== t141) {
    t142 = <li><span className="text-muted-foreground/40">{t139}{t141}</span></li>;
    $[230] = t139;
    $[231] = t141;
    $[232] = t142;
  } else {
    t142 = $[232];
  }
  const t143 = locale === "fr" ? "Bient\xF4t" : "Soon";
  let t144;
  if ($[233] !== t143) {
    t144 = <li><span className="text-muted-foreground/40">LinkedIn AI<Badge variant="secondary" className="ml-1.5 px-1.5 py-0 text-[9px]">{t143}</Badge></span></li>;
    $[233] = t143;
    $[234] = t144;
  } else {
    t144 = $[234];
  }
  let t145;
  if ($[235] !== t134 || $[236] !== t136 || $[237] !== t138 || $[238] !== t142 || $[239] !== t144) {
    t145 = <div>{t131}<ul className="space-y-1.5 text-xs text-muted-foreground">{t134}{t136}{t138}{t142}{t144}</ul></div>;
    $[235] = t134;
    $[236] = t136;
    $[237] = t138;
    $[238] = t142;
    $[239] = t144;
    $[240] = t145;
  } else {
    t145 = $[240];
  }
  const t146 = locale === "fr" ? "Ressources" : "Resources";
  let t147;
  if ($[241] !== t146) {
    t147 = <p className="mb-3 text-sm font-semibold">{t146}</p>;
    $[241] = t146;
    $[242] = t147;
  } else {
    t147 = $[242];
  }
  let t148;
  if ($[243] === Symbol.for("react.memo_cache_sentinel")) {
    t148 = <li><Link href="/blog" className="transition-colors hover:text-foreground">Blog</Link></li>;
    $[243] = t148;
  } else {
    t148 = $[243];
  }
  const t149 = locale === "fr" ? "Entretien Tech" : "Tech Interviews";
  let t150;
  if ($[244] !== t149) {
    t150 = <li><Link href="/entretien-tech" className="transition-colors hover:text-foreground">{t149}</Link></li>;
    $[244] = t149;
    $[245] = t150;
  } else {
    t150 = $[245];
  }
  const t151 = locale === "fr" ? "Entretien Consulting" : "Consulting Interviews";
  let t152;
  if ($[246] !== t151) {
    t152 = <li><Link href="/entretien-consulting" className="transition-colors hover:text-foreground">{t151}</Link></li>;
    $[246] = t151;
    $[247] = t152;
  } else {
    t152 = $[247];
  }
  const t153 = locale === "fr" ? "Entretien Finance" : "Finance Interviews";
  let t154;
  if ($[248] !== t153) {
    t154 = <li><Link href="/entretien-finance" className="transition-colors hover:text-foreground">{t153}</Link></li>;
    $[248] = t153;
    $[249] = t154;
  } else {
    t154 = $[249];
  }
  let t155;
  if ($[250] === Symbol.for("react.memo_cache_sentinel")) {
    t155 = <li><Link href="/faq" className="transition-colors hover:text-foreground">FAQ</Link></li>;
    $[250] = t155;
  } else {
    t155 = $[250];
  }
  const t156 = tObj.footer?.contact || "Contact";
  let t157;
  if ($[251] !== t156) {
    t157 = <li><Link href="mailto:contact@velyq.com" className="transition-colors hover:text-foreground">{t156}</Link></li>;
    $[251] = t156;
    $[252] = t157;
  } else {
    t157 = $[252];
  }
  let t158;
  if ($[253] !== t150 || $[254] !== t152 || $[255] !== t154 || $[256] !== t157) {
    t158 = <ul className="space-y-1.5 text-xs text-muted-foreground">{t148}{t150}{t152}{t154}{t155}{t157}</ul>;
    $[253] = t150;
    $[254] = t152;
    $[255] = t154;
    $[256] = t157;
    $[257] = t158;
  } else {
    t158 = $[257];
  }
  let t159;
  if ($[258] !== t147 || $[259] !== t158) {
    t159 = <div>{t147}{t158}</div>;
    $[258] = t147;
    $[259] = t158;
    $[260] = t159;
  } else {
    t159 = $[260];
  }
  const t160 = locale === "fr" ? "L\xE9gal" : "Legal";
  let t161;
  if ($[261] !== t160) {
    t161 = <p className="mb-3 text-sm font-semibold">{t160}</p>;
    $[261] = t160;
    $[262] = t161;
  } else {
    t161 = $[262];
  }
  const t162 = locale === "fr" ? "Mentions l\xE9gales" : "Legal notice";
  let t163;
  if ($[263] !== t162) {
    t163 = <li><Link href="/legal" className="transition-colors hover:text-foreground">{t162}</Link></li>;
    $[263] = t162;
    $[264] = t163;
  } else {
    t163 = $[264];
  }
  const t164 = locale === "fr" ? "CGU" : "Terms of service";
  let t165;
  if ($[265] !== t164) {
    t165 = <li><Link href="/terms" className="transition-colors hover:text-foreground">{t164}</Link></li>;
    $[265] = t164;
    $[266] = t165;
  } else {
    t165 = $[266];
  }
  const t166 = locale === "fr" ? "Confidentialit\xE9" : "Privacy";
  let t167;
  if ($[267] !== t166) {
    t167 = <li><Link href="/privacy" className="transition-colors hover:text-foreground">{t166}</Link></li>;
    $[267] = t166;
    $[268] = t167;
  } else {
    t167 = $[268];
  }
  let t168;
  if ($[269] !== t163 || $[270] !== t165 || $[271] !== t167) {
    t168 = <ul className="space-y-1.5 text-xs text-muted-foreground">{t163}{t165}{t167}</ul>;
    $[269] = t163;
    $[270] = t165;
    $[271] = t167;
    $[272] = t168;
  } else {
    t168 = $[272];
  }
  let t169;
  if ($[273] !== t161 || $[274] !== t168) {
    t169 = <div>{t161}{t168}</div>;
    $[273] = t161;
    $[274] = t168;
    $[275] = t169;
  } else {
    t169 = $[275];
  }
  let t170;
  if ($[276] !== t130 || $[277] !== t145 || $[278] !== t159 || $[279] !== t169) {
    t170 = <div className="mb-8 grid gap-8 md:grid-cols-4">{t130}{t145}{t159}{t169}</div>;
    $[276] = t130;
    $[277] = t145;
    $[278] = t159;
    $[279] = t169;
    $[280] = t170;
  } else {
    t170 = $[280];
  }
  let t171;
  if ($[281] === Symbol.for("react.memo_cache_sentinel")) {
    t171 = new Date().getFullYear();
    $[281] = t171;
  } else {
    t171 = $[281];
  }
  const t172 = tObj.footer?.rights || "All rights reserved";
  let t173;
  if ($[282] !== t172) {
    t173 = <p className="text-xs text-muted-foreground">© {t171} Velyq. {t172}.</p>;
    $[282] = t172;
    $[283] = t173;
  } else {
    t173 = $[283];
  }
  let t174;
  if ($[284] === Symbol.for("react.memo_cache_sentinel")) {
    t174 = <a href="https://discord.gg/hbevuxEKCq" target="_blank" rel="noopener noreferrer" className="text-muted-foreground/60 transition-colors hover:text-foreground"><svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" /></svg></a>;
    $[284] = t174;
  } else {
    t174 = $[284];
  }
  let t175;
  if ($[285] === Symbol.for("react.memo_cache_sentinel")) {
    t175 = <div className="flex items-center gap-3">{t174}<a href="https://www.linkedin.com/company/velyq" target="_blank" rel="noopener noreferrer" className="text-muted-foreground/60 transition-colors hover:text-foreground"><svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg></a></div>;
    $[285] = t175;
  } else {
    t175 = $[285];
  }
  let t176;
  if ($[286] !== t173) {
    t176 = <div className="flex flex-col items-center justify-between gap-3 border-t border-border/30 pt-6 sm:flex-row">{t173}{t175}</div>;
    $[286] = t173;
    $[287] = t176;
  } else {
    t176 = $[287];
  }
  let t177;
  if ($[288] !== t170 || $[289] !== t176) {
    t177 = <footer className="border-t border-border/30 py-10 sm:py-14"><div className="container mx-auto px-4">{t170}{t176}</div></footer>;
    $[288] = t170;
    $[289] = t176;
    $[290] = t177;
  } else {
    t177 = $[290];
  }
  let t178;
  if ($[291] !== t101 || $[292] !== t11 || $[293] !== t12 || $[294] !== t127 || $[295] !== t177 || $[296] !== t21 || $[297] !== t22 || $[298] !== t23 || $[299] !== t38 || $[300] !== t39 || $[301] !== t95) {
    t178 = <div className="flex min-h-screen flex-col">{t11}{t12}{t21}{t22}{t23}{t38}{t39}{t95}{t101}{t127}{t177}</div>;
    $[291] = t101;
    $[292] = t11;
    $[293] = t12;
    $[294] = t127;
    $[295] = t177;
    $[296] = t21;
    $[297] = t22;
    $[298] = t23;
    $[299] = t38;
    $[300] = t39;
    $[301] = t95;
    $[302] = t178;
  } else {
    t178 = $[302];
  }
  return t178;
}
function _temp3(feature_0, i_0) {
  return <li key={i_0} className="flex items-center gap-2.5 text-[13px]"><div className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-primary/20"><Check className="h-3 w-3 text-primary" strokeWidth={3} /></div>{feature_0}</li>;
}
function _temp2(feature, i) {
  return <li key={i} className="flex items-center gap-2.5 text-[13px]"><div className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-muted/50"><Check className="h-3 w-3 text-foreground/70" strokeWidth={3} /></div>{feature}</li>;
}
function _temp(link) {
  return <Link key={link.href} href={link.href} className={`link-slide inline-block text-center text-sm font-medium text-muted-foreground transition-colors hover:text-foreground ${link.minW}`}>{link.label}</Link>;
}